import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import ReferralCard from "./Referralcard";
import "../../styles/Referral.css";
import { FiSearch } from "react-icons/fi";
import { FaBuilding, FaUserTie, FaMapMarkerAlt } from "react-icons/fa";
import { getByQuery, create } from "../../api/API";
import { toast } from "react-toastify";
import Seo from "../Generic/Seo";
import LoadingSpinner from "../Generic/LoadingSpinner";
import { getCurrentUser, isLoggedIn } from "../../utility/UserUtils";
import GoogleAnalytics from "../Generic/GoogleAnalytics";
import ReferralJobPosted from "./ReferralJobPosted";

const NeedReferral = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invalidSearch, setInvalidSearch] = useState(false);

  const [search, setSearch] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [location, setLocation] = useState("");

  const [referralLoading, setReferralLoading] = useState(false);
  const [formData, setFormData] = useState({
    qualification: "",
    year: "",
    experience: "",
    company: "",
    message: "",
  });

  const seoData = {
    title: "WiSE Referral Program — Spread the Word & Earn Rewards",
    description:
      "Join the WiSE referral program: invite peers, students, or professionals to join our Women in Semiconductor community and earn recognition, rewards or special access.",
    keywords:
      "WiSE referral program, women in semiconductor referral, refer to WiSE, invite women in tech, rewards for referrals",
    pathSlug: "referral",
  };
  //Auto search when user types (debounced)
  useEffect(() => {
    const delay = setTimeout(() => {
      handleSearch();
    }, 300); //debounce delay 300ms

    return () => clearTimeout(delay);
  }, [search, company, role, location]);

  const handleSearch = async () => {
    try {
      if (!search && !company && !role && !location) {
        setMembers([]);
        setInvalidSearch(false);
        setLoading(false);
        return;
      }

      let query = "depth=2";

      //Only users who want to give referrals
      query += `where[canGiveReferral][equals]=true`;

      if (search) query += `&where[firstName][contains]=${search}`;

      if (company)
        query += `&where[experiences.companyName][contains]=${company}`;

      if (role) query += `&where[experiences.jobTitle][contains]=${role}`;

      if (location)
        query += `&where[experiences.location][contains]=${location}`;
      setLoading(true);
      const data = await getByQuery("members", query);
      let found = data.docs || [];

      //Exclude current user
      const currentUser = getCurrentUser();
      if (currentUser) {
        found = found.filter((member) => member.id !== currentUser.id);
      }

      setMembers(found);
      setInvalidSearch(found.length === 0);
    } catch (err) {
      console.error("Error fetching members:", err);
      setInvalidSearch(true);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleConnectClick = (person) => {
    if (!isLoggedIn()) {
      toast.info("Please login to get connected.");
      return;
    }
    setSelectedReferral(person);
  };

  const handleSubmitReferral = async () => {
    const { qualification, year, experience, company, message } = formData;

    if (!qualification || !year || !experience || !company) {
      toast.error("Please fill all required fields.");
      return;
    }

    const currentUser = getCurrentUser();
    if (!currentUser) {
      toast.error("You must be logged in to send a referral.");
      return;
    }

    setReferralLoading(true);
    try {
      await create("referrals", {
        referrer: currentUser.id,
        receiver: selectedReferral.id,
        notes: message || "Requested referral via frontend",
        qualification,
        year,
        experience,
        company,
      });

      toast.success("Referral request sent!");
      setSelectedReferral(null);
      setFormData({
        qualification: "",
        year: "",
        experience: "",
        company: "",
        message: "",
      });
    } catch (err) {
      console.error("Error sending referral:", err);
      toast.error("Failed to send referral.");
    } finally {
      setReferralLoading(false);
    }
  };
  return (
    <>
      <Seo data={seoData} />
      <GoogleAnalytics page={seoData.pathSlug} title={seoData.title} />
      <ReferralJobPosted />

      <div className="container my-5">
        {/* --- Suggestion / Title message at top --- */}
        <h5 className="text-secondary text-center mb-4">
          Search for referrals
        </h5>

        <p className="text-secondary text-center mb-5">
          Verified community referrals only — respectful, private and trackable
        </p>
        <h5 className="text-secondary text-left mb-4">Filters</h5>

        {/* Filters */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="input-group">
              <span className="input-group-text bg-white">
                <FiSearch />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="col-md-3">
            <div className="input-group">
              <span className="input-group-text bg-white">
                <FaBuilding />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>
          </div>

          <div className="col-md-3">
            <div className="input-group">
              <span className="input-group-text bg-white">
                <FaUserTie />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              />
            </div>
          </div>

          <div className="col-md-3">
            <div className="input-group">
              <span className="input-group-text bg-white">
                <FaMapMarkerAlt />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Referral Cards */}
        <div className="row g-4 justify-content-center mb-5">
          {loading ? (
            <LoadingSpinner />
          ) : !isLoggedIn() && (search || company || role || location) ? (
            <p className="text-center text-warning">
              Please login to view referral members.
            </p>
          ) : invalidSearch ? (
            <p className="text-center text-danger">
              No matching members found. Please try again.
            </p>
          ) : members.length > 0 ? (
            members.map((person, index) => (
              <ReferralCard
                key={person.id}
                person={{
                  id: person.id,
                  name: `${person.firstName} ${person.lastName}`,
                  role: person.experiences?.[0]?.jobTitle,
                  company: person.experiences?.[0]?.companyName,
                  email: person.email,
                  experience: person.experiences?.[0]?.years
                    ? `${person.experiences[0].years} years`
                    : "",
                  image:
                    person.avatar?.url || "/assets/images/default-avatar.png",
                  linkedin: person.linkedin,
                }}
                isHovered={hoveredIndex === index}
                onConnect={() =>
                  handleConnectClick({
                    id: person.id,
                    name: `${person.firstName} ${person.lastName}`,
                    role: person.jobTitle,
                    email: person.email,
                    image: person.avatar?.url || "/assets/images/default.png",
                  })
                }
                onHover={() => setHoveredIndex(index)}
                onLeave={() => setHoveredIndex(null)}
                cardPosition={(index + 1) % 4 === 0 ? "last" : "normal"}
              />
            ))
          ) : (
            <></>
          )}
        </div>

        {/* Modal */}
        <Modal
          show={!!selectedReferral}
          onHide={() => setSelectedReferral(null)}
          centered
        >
          <div style={{ position: "relative" }}>
            {/* Overlay Loader */}
            {referralLoading && (
              <div className="loading-overlay">
                <LoadingSpinner text="Sending referral request..." />
              </div>
            )}

            {selectedReferral && (
              <>
                <Modal.Header closeButton className="bg-primary text-secondary">
                  <div className="d-flex align-items-center">
                    <img
                      src={selectedReferral.image}
                      alt={selectedReferral.name}
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        objectFit: "contain",
                        marginRight: "15px",
                      }}
                    />
                    <div>
                      <h5 className="mb-0">{selectedReferral.name}</h5>
                      <small className="text-muted">
                        {selectedReferral.role}
                      </small>
                    </div>
                  </div>
                </Modal.Header>

                <Modal.Body>
                  <form>
                    <div className="mb-2">
                      <label>Email*</label>
                      <input
                        type="email"
                        className="form-control"
                        defaultValue={selectedReferral.email}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="mb-2">
                      <label>Qualification*</label>
                      <input
                        type="text"
                        name="qualification"
                        className="form-control"
                        value={formData.qualification}
                        onChange={handleInputChange}
                        required
                        disabled={referralLoading}
                      />
                    </div>

                    <div className="mb-2">
                      <label>Year of Passing*</label>
                      <input
                        type="text"
                        name="year"
                        className="form-control"
                        value={formData.year}
                        onChange={handleInputChange}
                        required
                        disabled={referralLoading}
                      />
                    </div>

                    <div className="mb-2">
                      <label>Experience*</label>
                      <input
                        type="text"
                        name="experience"
                        className="form-control"
                        value={formData.experience}
                        onChange={handleInputChange}
                        required
                        disabled={referralLoading}
                      />
                    </div>

                    <div className="mb-2">
                      <label>Current Company*</label>
                      <input
                        type="text"
                        name="company"
                        className="form-control"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        disabled={referralLoading}
                      />
                    </div>

                    <div className="mb-2">
                      <label>Message</label>
                      <textarea
                        name="message"
                        className="form-control"
                        rows="3"
                        placeholder="Hi, can you help me with a referral?"
                        value={formData.message}
                        onChange={handleInputChange}
                        disabled={referralLoading}
                      />
                    </div>
                  </form>
                </Modal.Body>

                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => setSelectedReferral(null)}
                    disabled={referralLoading}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="primary"
                    onClick={handleSubmitReferral}
                    disabled={referralLoading}
                  >
                    Let's Connect
                  </Button>
                </Modal.Footer>
              </>
            )}
          </div>
        </Modal>
      </div>
    </>
  );
};

export default NeedReferral;
